// CAUTION: Imports in this file will contribute to the module.tsx bundle size
import {
  PluginExtensionPoints,
  type PluginExtensionAddedLinkConfig,
  type PluginExtensionPanelContext,
} from '@grafana/data';
import { interpolateQueryExpr } from '@grafana/prometheus';
import { getTemplateSrv } from '@grafana/runtime';
import { type DataQuery } from '@grafana/schema';
import { parser } from '@prometheus-io/lezer-promql';

import { PLUGIN_BASE_URL } from 'shared/constants/plugin';
import { ROUTES } from 'shared/constants/routes';

import { parseMatcher } from './parseMatcher';
import { processLabelMatcher, type ParsedPromQLQuery, type PromQLLabelMatcher } from '../shared/utils/utils.promql';

// TODO: Remove this once @grafana/data exports the group field type (https://github.com/grafana/grafana/pull/116481)
type PluginExtensionAddedLinkConfigWithGroup<T extends object = object> = PluginExtensionAddedLinkConfig<T> & {
  group?: { name: string; icon: string };
};

const PRODUCT_NAME = 'Grafana Metrics Drilldown';
const title = `Open in ${PRODUCT_NAME}`;
const description = `Open current query in the ${PRODUCT_NAME} view`;
const icon = 'gf-prometheus';

const ASSISTANT_TARGET_V0 = 'grafana-metricsdrilldown-app/grafana-assistant-app/navigateToDrilldown/v0-alpha';
const ASSISTANT_TARGET_V1 = 'grafana-assistant-app/navigateToDrilldown/v1';

const ADHOC_URL_DELIMITER = '|';

export const linkConfigs: Array<PluginExtensionAddedLinkConfigWithGroup<PluginExtensionPanelContext>> = [
  {
    title,
    description,
    icon,
    group: { name: '${root}/Metrics drilldown', icon: 'code-branch' },
    path: createAppUrl(ROUTES.Drilldown),
    targets: [PluginExtensionPoints.DashboardPanelMenu],
    configure: configureDrilldownLink,
  },
  {
    title,
    description,
    icon,
    path: createAppUrl(ROUTES.Drilldown),
    targets: [
      PluginExtensionPoints.ExploreToolbarAction,
      PluginExtensionPoints.AlertingRuleQueryEditor,
      ASSISTANT_TARGET_V1,
    ],
    configure: configureDrilldownLink,
  },
  {
    targets: [ASSISTANT_TARGET_V0],
    title: 'Navigate to metrics drilldown',
    description: 'Build a url path to the metrics drilldown',
    path: createAppUrl(ROUTES.Drilldown),
    configure: (context) => {
      if (typeof context === 'undefined') {
        return;
      }

      const { navigateToMetrics, datasource_uid, label_filters, metric, start, end } =
        context as unknown as GrafanaAssistantMetricsDrilldownContext;
      // parse the labels to the PromQL format
      const parsedLabels = parseFiltersToLabelMatchers(label_filters);
      // create the PromURLObject for building params
      const promURLObject = createPromURLObject(datasource_uid, parsedLabels, metric, start, end);
      // build the params for the navigateToMetrics
      const params = navigateToMetrics ? buildNavigateToMetricsParams(promURLObject) : undefined;

      return {
        path: createAppUrl(ROUTES.Drilldown, params),
      };
    },
  },
];

// Configure function for drilldown link
export function configureDrilldownLink<T extends PluginExtensionPanelContext>(context?: T) {
  if (typeof context === 'undefined') {
    return;
  }

  if ('pluginId' in context && context.pluginId !== 'timeseries') {
    return;
  }

  const queries = context.targets.filter(isPromQuery);

  if (!queries.length) {
    return;
  }

  const url = buildDrilldownUrl(context);

  return {
    path: url || createAppUrl(ROUTES.Drilldown),
  };
}

// URL building function for drilldown links
export function buildDrilldownUrl<T extends PluginExtensionPanelContext>(context?: T): string | null {
  if (typeof context === 'undefined') {
    return null;
  }

  if ('pluginId' in context && context.pluginId !== 'timeseries') {
    return null;
  }

  const queries = context.targets.filter(isPromQuery);

  if (!queries.length) {
    return null;
  }

  const prometheusQuery = queries[0] as PromQuery;

  const templateSrv = getTemplateSrv();
  const datasourceUid = templateSrv.replace(prometheusQuery.datasource?.uid, context.scopedVars);

  // allow the user to navigate to the drilldown without a query (metrics reducer view)
  if (!prometheusQuery.expr) {
    return createAppUrl(ROUTES.Drilldown);
  }

  const expr = templateSrv.replace(prometheusQuery.expr, context.scopedVars, interpolateQueryExpr);

  try {
    const { metric, labels, hasErrors, errors } = parsePromQLQuery(expr);

    if (hasErrors) {
      import('shared/logger/logger').then(({ logger }) =>
        logger.warn(`PromQL query has parsing errors: ${errors.join(', ')}`)
      );
    }

    const timeRange =
      'timeRange' in context &&
      typeof context.timeRange === 'object' &&
      context.timeRange != null &&
      'from' in context.timeRange &&
      'to' in context.timeRange
        ? (context.timeRange as { from: string; to: string })
        : undefined;

    const promURLObject = createPromURLObject(datasourceUid, labels, metric, timeRange?.from, timeRange?.to);

    const params = buildNavigateToMetricsParams(promURLObject);

    return createAppUrl(ROUTES.Drilldown, params);
  } catch (error) {
    import('shared/logger/logger').then(({ logger }) =>
      logger.error(new Error(`[Metrics Drilldown] Error parsing PromQL query: ${error}`))
    );

    return createAppUrl(ROUTES.Drilldown);
  }
}

// PromQL parser using lezer parser
export function parsePromQLQuery(expr: string): ParsedPromQLQuery {
  const tree = parser.parse(expr);
  let metric = '';
  const labels: PromQLLabelMatcher[] = [];
  let hasErrors = false;
  const errors: string[] = [];

  // Use tree.iterate() - much simpler than manual cursor traversal
  tree.iterate({
    enter: (node) => {
      // Check if this is an error node
      if (node.type.isError || node.name === '⚠') {
        hasErrors = true;
        const errorText = expr.slice(node.from, node.to);
        const errorMsg = errorText
          ? `Parse error at position ${node.from}-${node.to}: "${errorText}"`
          : `Parse error at position ${node.from}`;
        errors.push(errorMsg);
      }

      // Get the first metric name from any VectorSelector > Identifier
      if (!metric && node.name === 'Identifier' && node.node.parent?.type.name === 'VectorSelector') {
        metric = expr.slice(node.from, node.to);
      }

      // Extract label matchers using helper function
      const labelData = processLabelMatcher(node, expr);
      if (labelData) {
        labels.push(labelData);
      }
    },
  });

  return { metric, labels, hasErrors, errors };
}

/**
 * Scenes adhoc variable filters requires a | delimiter
 * between the label, operator, and value (see AdHocFiltersVariableUrlSyncHandler.ts in Scenes)
 */
function filterToUrlParameter(filter: PromQLLabelMatcher): [UrlParameterType, string] {
  return [
    UrlParameters.Filters,
    `${filter.label}${ADHOC_URL_DELIMITER}${filter.op}${ADHOC_URL_DELIMITER}${escapeUrlPipeDelimiters(filter.value)}`,
  ] as [UrlParameterType, string];
}

// Type for the metrics drilldown context from Grafana Assistant
export type GrafanaAssistantMetricsDrilldownContext = {
  navigateToMetrics: boolean;
  datasource_uid: string;
  label_filters?: string[];
  metric?: string;
  start?: string;
  end?: string;
};

type PromURLObject = {
  datasource_uid?: string;
  label_filters?: PromQLLabelMatcher[];
  metric?: string;
  start?: string;
  end?: string;
};

export function createPromURLObject(
  datasource_uid?: string,
  label_filters?: PromQLLabelMatcher[],
  metric?: string,
  start?: string,
  end?: string
): PromURLObject {
  return {
    datasource_uid,
    label_filters: label_filters ?? [],
    metric,
    start,
    end,
  };
}

export function buildNavigateToMetricsParams(promURLObject: PromURLObject): URLSearchParams {
  const { metric, start, end, datasource_uid, label_filters } = promURLObject;

  const filters = label_filters ?? [];

  // Use the structured context data to build parameters
  return appendUrlParameters([
    [UrlParameters.Metric, metric],
    [UrlParameters.TimeRangeFrom, start],
    [UrlParameters.TimeRangeTo, end],
    [UrlParameters.DatasourceId, datasource_uid],
    ...filters.map(filterToUrlParameter),
  ]);
}

export function parseFiltersToLabelMatchers(label_filters?: string[]): PromQLLabelMatcher[] {
  if (!label_filters) {
    return [];
  }

  return label_filters.map((filter) => {
    const matcher = parseMatcher(filter);
    return {
      label: matcher.key,
      op: matcher.operator,
      value: matcher.value,
    };
  });
}

export function createAppUrl(route: string, urlParams?: URLSearchParams): string {
  const urlParamsAsString = urlParams ? `?${urlParams.toString()}` : '';
  return `${PLUGIN_BASE_URL}/${route}${urlParamsAsString}`;
}

// We can't use `src/shared.ts` vars here because of the impacts of its imports on the module.tsx bundle size
export const UrlParameters = {
  TimeRangeFrom: 'from',
  TimeRangeTo: 'to',
  Metric: 'metric',
  DatasourceId: `var-ds`,
  Filters: `var-filters`,
} as const;

type UrlParameterType = (typeof UrlParameters)[keyof typeof UrlParameters];

export function appendUrlParameters(
  params: Array<[UrlParameterType, string | undefined]>,
  initialParams?: URLSearchParams
): URLSearchParams {
  const searchParams = new URLSearchParams(initialParams?.toString());

  params.forEach(([key, value]) => {
    if (value) {
      searchParams.append(key, value);
    }
  });

  return searchParams;
}

type PromQuery = DataQuery & { expr: string };

function isPromQuery(query: DataQuery): query is PromQuery {
  const { datasource } = query;
  return datasource?.type === 'prometheus';
}

// Need to export this function from scenes because importing scenesUtils is increasing the bundle entry point size by 522.51kB
export function escapeUrlPipeDelimiters(value: string | undefined): string {
  if (value == null) {
    return '';
  }

  // Replace the pipe due to using it as a filter separator
  return /\|/g[Symbol.replace](value, '__gfp__');
}
